// On-hand Stock — lot-level ingredient stock the canner (or owner) maintains. Drives the ordering
// calc's on-hand subtraction. Uses the /stock endpoint so it shares one code path with the canner.
import { useMemo, useState } from "react";
import type { StockLot } from "@shared/types.ts";
import { onHandByIngredient } from "@shared/engine.ts";
import { useData } from "../state/store.tsx";
import { api } from "../lib/api.ts";
import { id as newId } from "../lib/ids.ts";
import { StockPanel, type BulkChange } from "../components/StockPanel.tsx";

export default function Stock() {
  const { data, reload, mutate } = useData();
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const massUnit = data.business.massUnit || "lb";

  const act = async (action: "add" | "update" | "delete", lot: Partial<StockLot>) => {
    setBusy(true);
    try {
      await api.stock({ businessId: data.business.id, action, lot });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  // Bulk stock-take — one document save for all changes (set qty, or 0 to remove).
  const onBulk = async (changes: BulkChange[]) => {
    if (changes.length === 0) return;
    setBusy(true);
    try {
      await mutate((draft) => {
        for (const c of changes) {
          if (c.lotId) {
            if (c.lbs > 0) {
              const lot = draft.stock.find((l) => l.id === c.lotId);
              if (lot) { lot.lbs = c.lbs; lot.origQty = c.lbs; lot.uom = massUnit; lot.lotNumber = c.lotNumber || undefined; lot.expiryDate = c.expiryDate || undefined; lot.receivedDate = c.receivedDate || undefined; }
            } else { draft.stock = draft.stock.filter((l) => l.id !== c.lotId); }
          } else if (c.lbs > 0) {
            draft.stock.push({ id: newId("lot"), ingredient: c.ingredient, lbs: c.lbs, origQty: c.lbs, uom: massUnit, lotNumber: c.lotNumber || undefined, expiryDate: c.expiryDate || undefined, receivedDate: c.receivedDate || undefined, source: "manual", createdAt: Date.now() });
          }
        }
      });
    } finally {
      setBusy(false);
    }
  };

  const onHand = useMemo(() => onHandByIngredient(data.stock), [data.stock]);

  return (
    <StockPanel
      ingredients={data.ingredients.map((i) => i.name)}
      lots={data.stock}
      onHand={onHand}
      busy={busy}
      massUnit={massUnit}
      onAdd={(lot) => act("add", lot)}
      onUpdate={(lot) => act("update", lot)}
      onDelete={(idv) => act("delete", { id: idv })}
      onBulk={onBulk}
    />
  );
}
